import { useEffect, useState } from 'react';
import axios from 'axios';
import SideBarSkeleton from '../../Component/SideBarSkeleton';
import { useSelector } from 'react-redux';
import
{
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { apiUrl } from '../../config/apiUrl';
import { outlookAuth, startOutlookLogin } from '../../utils/outlook';
import { Link, redirect } from 'react-router-dom';

/**
 * Outlook component displays the user's Outlook calendar events and provides login/refresh functionality.
 * Handles authentication, event fetching, and displays loading states and errors.
 *
 * @component
 */
const Outlook = () =>
{
  const [ events, setEvents ] = useState( [] );
  const [ loading, setLoading ] = useState( false );

  const userId = useSelector( state => state.authReducer.user?._id );
  const accessToken = useSelector( state => state.authReducer.access_token );
  const isAuthenticated = useSelector( state => state.authReducer.outlookAuthenticated );

  useEffect( () =>
  {
    if ( isAuthenticated && userId )
    {
      fetchEvents();
    }
  }, [ isAuthenticated, userId ] );

  /**
   * Redirects the user to the Outlook login page for authentication.
   */
  const handleLogin = () =>
  {
    if ( !userId )
    {
      alert( 'User ID not found in localStorage' );
      return;
    }
    startOutlookLogin( accessToken );
  };

  /**
   * Fetches Outlook calendar events for the authenticated user.
   * Updates the events state and handles loading/errors.
   *
   * @async
   * @returns {Promise<void>}
   */
  const fetchEvents = async () =>
  {
    try
    {
      setLoading( true );
      const response = await axios.get( `${apiUrl}/outlook/events`, outlookAuth( accessToken ) );
      setEvents( response.data );
    } catch ( error )
    {
      console.error( 'Error fetching events:', error );
      alert( 'Could not fetch Outlook calendar events' );
    } finally
    {
      setLoading( false );
    }
  };

  return (
    <>
      <style>
        {`
        body, html {
          overflow-y: hidden !important;
        }
        `}
      </style>
      <Box sx={{ p: 4 }} style={
        {
          overflowY: 'scroll'
        }
      }>
        <Box sx={{ mb: 3 }}>
          {!isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogin}
              sx={{
                backgroundColor: '#0072C6',
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#005A9E',
                },
                padding: '10px 20px',
                fontSize: '16px',
              }}
            >
              Login with Outlook
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={fetchEvents}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                backgroundColor: '#0072C6',
                fontFamily: "'Open Sans', sans-serif",
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#005fa3', // ~15% darker
                },
                padding: '10px 20px',
                fontSize: '16px',
              }}
            >
              {loading ? 'Fetching Events...' : 'Refresh Events'}
            </Button>
          )}
        </Box>

        {isAuthenticated ? (
          loading ? (
            <List>
              {[ 1, 2, 3, 4, 5 ].map( ( index ) => (
                <ListItem key={index} component={Paper} sx={{ mb: 1, p: 2 }}>
                  <ListItemText
                    primary={
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={24}
                        sx={{ mb: 1 }}
                      />
                    }
                    secondary={
                      <Skeleton variant="text" width="40%" height={20} />
                    }
                  />
                </ListItem>
              ) )}
            </List>
          ) : events.length === 0 ? (
            <Typography variant="body1">No events found.</Typography>
          ) : (
            <List>
              {events.map( ( event ) =>
              {
                console.log( "event displayNam  ===>", event.location.displayName )
                return (
                  <ListItem style={{ cursor: 'pointer' }} key={event.id} onClick={() => window.open( event.location.displayName, '_blank' )} component={Paper} sx={{ mb: 1, p: 2 }}>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {event.subject}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {new Date( event.start.dateTime ).toLocaleString()} to{' '}
                          {new Date( event.end.dateTime ).toLocaleString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                )
              } )}
            </List>
          )
        ) : (
          <Typography variant="body1" color="text.secondary">
            You're not logged in to Outlook
          </Typography>
        )}
      </Box>
    </>
  );
};

export default Outlook;
